import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';

/**
 * Fields:
 *  { name, label, type: 'text'|'select'|'checkbox'|'section', options?, required?, placeholder?, full?, showIf?: {fieldName: value} }
 * `options` for a select field can also be a function `(data) => options[]`,
 * so its choices can depend on another field's current value (e.g. Desa
 * options depending on the selected Kecamatan) - a "cascading select".
 */
// Radix Select.Item forbids an empty-string value, so option value '' (meaning
// "no selection" / "not restricted") is mapped to this sentinel in the UI only.
const EMPTY_OPTION_VALUE = '__empty__';
const EntityFormDialog = ({ open, onOpenChange, title, description, fields, initialData, onSubmit, extraHeader, alertBanner, submitDisabled = false, onDataChange }) => {
  const [data, setData] = useState(initialData || {});
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { setData(initialData || {}); }, [initialData, open]);

  const updateField = (key, val) => {
    const next = { ...data, [key]: val };
    setData(next);
    if (onDataChange) onDataChange(next);
  };

  const isFieldVisible = (f) => {
    if (!f.showIf) return true;
    return Object.entries(f.showIf).every(([key, val]) =>
      Array.isArray(val) ? val.includes(data[key]) : data[key] === val
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitDisabled) return;
    setSaving(true);
    const ok = await onSubmit(data);
    setSaving(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {extraHeader}
        {alertBanner}
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          {fields.map((f, idx) => {
            if (!isFieldVisible(f)) return null;

            if (f.type === 'section') {
              return (
                <div key={idx} className="col-span-2 pt-3 pb-1 border-t border-gray-100 mt-2 first:border-t-0 first:mt-0 first:pt-0">
                  <p className="text-xs font-extrabold uppercase tracking-wider text-orange-600">{f.label}</p>
                  {f.description && <p className="text-xs text-gray-500 font-medium mt-0.5">{f.description}</p>}
                </div>
              );
            }

            if (f.type === 'checkbox') {
              return (
                <div key={f.name} className={`col-span-2 flex items-center gap-3 p-3 rounded-xl bg-orange-50/50 border border-orange-100`}>
                  <Checkbox
                    id={f.name}
                    checked={!!data[f.name]}
                    onCheckedChange={(v) => updateField(f.name, !!v)}
                  />
                  <label htmlFor={f.name} className="text-sm font-bold cursor-pointer flex-1">{f.label}
                    {f.hint && <span className="block text-xs font-medium text-gray-500 mt-0.5">{f.hint}</span>}
                  </label>
                </div>
              );
            }

            return (
              <div key={f.name} className={f.full ? 'col-span-2' : ''}>
                <Label className="font-semibold text-sm mb-1.5 block">{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
                {f.type === 'select' ? (() => {
                  const opts = typeof f.options === 'function' ? f.options(data) : (f.options || []);
                  const rawVal = data[f.name];
                  let selectVal = rawVal || EMPTY_OPTION_VALUE;
                  let selectedOption = null;
                  if (rawVal && rawVal !== EMPTY_OPTION_VALUE) {
                    const directMatch = opts.find(o => o.value === rawVal);
                    if (directMatch) {
                      selectVal = directMatch.value;
                      selectedOption = directMatch;
                    } else {
                      const caseMatch = opts.find(o => String(o.value || '').trim().toLowerCase() === String(rawVal).trim().toLowerCase());
                      if (caseMatch) {
                        selectVal = caseMatch.value;
                        selectedOption = caseMatch;
                      } else {
                        selectVal = rawVal;
                        selectedOption = { value: rawVal, label: rawVal };
                      }
                    }
                  }
                  const allOpts = [...opts];
                  if (selectedOption && !allOpts.some(o => o.value === selectedOption.value)) {
                    allOpts.unshift(selectedOption);
                  }
                  return (
                    <Select
                      key={`${f.name}-${f.name === 'desa' ? (data.kecamatan || '') : ''}-${selectVal}`}
                      value={selectVal}
                      onValueChange={(v) => updateField(f.name, v === EMPTY_OPTION_VALUE ? '' : v)}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder={f.placeholder || `Pilih ${f.label}`}>
                          {selectedOption ? selectedOption.label : undefined}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {allOpts.map(o => (
                          <SelectItem key={o.value || EMPTY_OPTION_VALUE} value={o.value || EMPTY_OPTION_VALUE}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                })() : (
                  <Input
                    value={data[f.name] || ''}
                    onChange={(e) => updateField(f.name, e.target.value)}
                    placeholder={f.placeholder}
                    required={f.required}
                    className="h-10"
                  />
                )}
                {f.hint && <p className="text-xs text-gray-500 font-medium mt-1">{f.hint}</p>}
              </div>
            );
          })}
          <DialogFooter className="col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="font-bold">Batal</Button>
            <Button type="submit" disabled={saving || submitDisabled} className="bg-orange-500 hover:bg-orange-600 font-bold">{saving ? 'Menyimpan...' : 'Simpan'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EntityFormDialog;
